import {
  createInMemoryAdaptiveProfileRepository,
  createInMemoryMatchRepository,
  createInMemoryPreferencesRepository,
  createInMemoryRulesetRepository,
  createInMemoryStatsRepository,
  DEFAULT_ADAPTIVE_PROFILE,
  DEFAULT_USER_PREFERENCES,
  type AdaptivePlayerProfile,
  type UserPreferences,
} from '@texas42/app-model';
import type { AppRepositories } from '../../app/types.js';
import { openTexas42Database, type Texas42Database } from './db.js';
import { writeDatabaseMetadata } from './metadata.js';
import { createIndexedDbMatchRepository } from './repositories/match-repository.js';
import { createIndexedDbRulesetRepository } from './repositories/ruleset-repository.js';
import {
  createIndexedDbAdaptiveProfileRepository,
  createIndexedDbPreferencesRepository,
} from './repositories/preferences-repository.js';
import { createIndexedDbStatsRepository } from './repositories/stats-repository.js';
import { WriteQueue } from './write-queue.js';
import { MatchPersistenceCoordinator } from './match-persistence.js';

export interface RepositoryBootstrapOptions {
  preferences?: UserPreferences;
  adaptiveProfile?: AdaptivePlayerProfile;
}

export interface AppPersistenceBundle {
  repositories: AppRepositories;
  db: Texas42Database | null;
  queue: WriteQueue;
  matchPersistence: MatchPersistenceCoordinator;
}

export function createMemoryRepositories(
  options: RepositoryBootstrapOptions = {},
): AppPersistenceBundle {
  const repositories: AppRepositories = {
    matches: createInMemoryMatchRepository(),
    rulesets: createInMemoryRulesetRepository(),
    preferences: createInMemoryPreferencesRepository(
      options.preferences ?? DEFAULT_USER_PREFERENCES,
    ),
    stats: createInMemoryStatsRepository(),
    adaptiveProfile: createInMemoryAdaptiveProfileRepository(
      options.adaptiveProfile ?? DEFAULT_ADAPTIVE_PROFILE,
    ),
  };

  const queue = new WriteQueue();
  return {
    repositories,
    db: null,
    queue,
    matchPersistence: new MatchPersistenceCoordinator(
      repositories.matches,
      repositories.stats,
      queue,
    ),
  };
}

export async function createIndexedDbRepositories(
  options: RepositoryBootstrapOptions = {},
  idbFactory?: typeof globalThis.indexedDB,
): Promise<AppPersistenceBundle> {
  const db = await openTexas42Database(idbFactory);
  await writeDatabaseMetadata(db);

  const queue = new WriteQueue();
  const repositories: AppRepositories = {
    matches: createIndexedDbMatchRepository(db, queue),
    rulesets: createIndexedDbRulesetRepository(db, queue),
    preferences: createIndexedDbPreferencesRepository(
      db,
      queue,
      options.preferences ?? DEFAULT_USER_PREFERENCES,
    ),
    stats: createIndexedDbStatsRepository(db, queue),
    adaptiveProfile: createIndexedDbAdaptiveProfileRepository(
      db,
      queue,
      options.adaptiveProfile ?? DEFAULT_ADAPTIVE_PROFILE,
    ),
  };

  return {
    repositories,
    db,
    queue,
    matchPersistence: new MatchPersistenceCoordinator(
      repositories.matches,
      repositories.stats,
      queue,
    ),
  };
}

export function shouldUseIndexedDbPersistence(): boolean {
  if (import.meta.env.VITEST) {
    return false;
  }
  return typeof globalThis.indexedDB !== 'undefined';
}

export async function bootstrapAppPersistence(
  options: RepositoryBootstrapOptions = {},
): Promise<AppPersistenceBundle> {
  if (!shouldUseIndexedDbPersistence()) {
    return createMemoryRepositories(options);
  }

  try {
    return await createIndexedDbRepositories(options);
  } catch {
    return createMemoryRepositories(options);
  }
}
